import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

export default function AvoidHardcodedIdsPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-gray-500 hover:text-gray-700">
                Best Practices
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Avoid Hardcoded IDs</li>
          </ol>
        </nav>

        {/* Back Button */}
        <Link 
          to={`${basePath}/help/best-practices`} 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Best Practices
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Complete Guide to Avoiding Hardcoded IDs in Flows
          </h1>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
              Intermediate
            </span>
            <span>8 min read</span>
            <span>Development Best Practice</span>
          </div>
        </div>

        {/* Golden Rule */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">The Golden Rule</h3>
              <p className="text-yellow-800 font-medium">
                If it starts with "00..." or "01..." - STOP! There's a better way.
              </p>
            </div>
          </div>
        </div>

        {/* Why It Matters */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why Hardcoded IDs Break Your Flows
          </h2>
          <p className="text-gray-700 mb-4">
            Salesforce generates unique IDs for every record in every environment. When you copy 
            <code className="bg-gray-100 px-2 py-1 rounded text-sm mx-1">00530000003xqAB</code> 
            from production and paste it into a flow, you're creating a time bomb that will explode during deployment.
          </p>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-red-800">
              <strong>Common failure scenario:</strong> A flow works perfectly in sandbox but fails
              immediately in production because the hardcoded User ID doesn't exist there.
            </p>
          </div>
        </section>

        {/* Quick Reference Table */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quick Reference Guide</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    What You're Assigning
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    <XCircleIcon className="h-4 w-4 text-red-600 inline mr-1" />
                    Wrong Way
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 inline mr-1" />
                    Right Way
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">User</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-red-50 px-2 py-1 rounded">OwnerId = "005..."</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-green-50 px-2 py-1 rounded">Use Queue or Username</code>
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Record Type</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-red-50 px-2 py-1 rounded">RecordTypeId = "012..."</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-green-50 px-2 py-1 rounded">Query by DeveloperName</code>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Queue</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-red-50 px-2 py-1 rounded">OwnerId = "00G..."</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-green-50 px-2 py-1 rounded">Query by DeveloperName</code>
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Profile</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-red-50 px-2 py-1 rounded">ProfileId = "00e..."</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <code className="bg-green-50 px-2 py-1 rounded">Query by Name</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Decision Tree */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Decision Tree</h2>
          <div className="bg-gray-100 rounded-lg p-6">
            <div className="font-mono text-sm space-y-2">
              <div>Need to reference a specific...</div>
              <div className="ml-4">├── Person? → Use Queue (add person as member)</div>
              <div className="ml-4">├── Team? → Use Queue or Public Group</div>
              <div className="ml-4">├── Record Type? → Query by DeveloperName</div>
              <div className="ml-4">├── Configuration Value? → Use Custom Metadata</div>
              <div className="ml-4">└── Complex Logic? → Use Assignment Rules</div>
            </div>
          </div>
        </section>

        {/* Step by Step Fixes */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Step-by-Step Fixes</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Finding Hardcoded IDs</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Look for 15-18 character strings starting with "00"
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Check all Assignment elements
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Review Decision elements
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Inspect formula fields
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Identifying the Type</h3>
              <p className="text-gray-700 mb-3">First 3 characters tell you the object type:</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">005</code>
                  <span className="ml-2 text-gray-600">= User</span>
                </div>
                <div className="flex items-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">012</code>
                  <span className="ml-2 text-gray-600">= Record Type</span>
                </div>
                <div className="flex items-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">00G</code>
                  <span className="ml-2 text-gray-600">= Queue/Group</span>
                </div>
                <div className="flex items-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">00e</code>
                  <span className="ml-2 text-gray-600">= Profile</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Implementing the Fix</h3>
              <ol className="space-y-2 text-gray-700">
                <li>1. Identify what the ID represents</li>
                <li>2. Choose appropriate pattern from reference guide</li>
                <li>3. Update flow to use dynamic reference</li>
                <li>4. Test in sandbox</li>
                <li>5. Deploy safely!</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Common Patterns */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Common Patterns</h2>
          
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Pattern 1: Regional Assignment</h3>
              <pre className="bg-white p-4 rounded text-sm overflow-x-auto">
{`If Region = "West" → Assign to "West_Sales_Queue"
If Region = "East" → Assign to "East_Sales_Queue"`}
              </pre>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 mb-2">Pattern 2: Dynamic Record Types</h3>
              <pre className="bg-white p-4 rounded text-sm overflow-x-auto">
{`Get Records: RecordType
Where: DeveloperName = {!FormulaForRecordType}
Use: {!Get_RecordType.Id}`}
              </pre>
            </div>
          </div>
        </section>

        {/* Testing Checklist */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            <ClipboardDocumentCheckIcon className="h-6 w-6 inline mr-2" />
            Deployment Checklist
          </h2>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <ul className="space-y-3">
              <li className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-blue-600 rounded mr-3" />
                <span>No IDs starting with "00"</span>
              </li>
              <li className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-blue-600 rounded mr-3" />
                <span>All queries use names</span>
              </li>
              <li className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-blue-600 rounded mr-3" />
                <span>Custom Metadata for config</span>
              </li>
              <li className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-blue-600 rounded mr-3" />
                <span>Queues for user assignment</span>
              </li>
              <li className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-blue-600 rounded mr-3" />
                <span>Test in target environment</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Resources */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Resources</h2>
          <ul className="space-y-2">
            <li>
              <a 
                href="https://help.salesforce.com/s/articleView?id=sf.setting_up_queues.htm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                Salesforce Queue Documentation →
              </a>
            </li>
            <li>
              <a 
                href="https://help.salesforce.com/s/articleView?id=sf.custommetadatatypes_overview.htm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                Custom Metadata Types →
              </a>
            </li>
            <li>
              <a 
                href="https://architect.salesforce.com/design/decision-guides/trigger-automation/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                Flow Best Practices →
              </a>
            </li>
          </ul>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Found hardcoded IDs in your flows? Use FlowDocs to identify and fix them automatically.
          </p>
          <Link 
            to={`${basePath}/flows`} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Check Your Flows
          </Link>
        </div>
      </div>
    </div>
  );
}